import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { List, FlexboxGrid, IconButton, Badge, useToaster, Message, Panel, Nav, Input, InputNumber } from 'rsuite';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { UserContext } from '../../_context/UserContext';
import { ListHeader, MainDivider } from '../../admin/helpers/Admin_StyledComponents';
import axiosInstance from '../../../../../appHelpers/axiosInstance';
import AddOutlineIcon from '@rsuite/icons/AddOutline';
import SearchIcon from '@rsuite/icons/Search';
import { SeverityAlert } from '../../../../../appHelpers/appCommonSC';
import { LoadingData } from './LoadingData';

export const TemplatedAlertsFC = ({ metadata, refresh, systemId, isAdminMode = false, isHHM = false, adminViewToggle, setAdminViewToggle }) => {
   const { id: urlId } = useParams();
   const id = systemId || urlId;
   const { getAccessTokenSilently } = useAuth0();
   const toaster = useToaster();
   const { user } = useContext(UserContext);
   const [templates, setTemplates] = useState([]);
   const [filteredTemplates, setFilteredTemplates] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [editingTemplates, setEditingTemplates] = useState({});
   const [existingAlerts, setExistingAlerts] = useState([]);
   const [applyingTemplates, setApplyingTemplates] = useState({});

   const getAlertTemplates = async () => {
      setLoading(true);
      try {
         const token = await getAccessTokenSilently({ audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}` });

         const response = await axiosInstance({
            method: 'get',
            url: `/alert/templates/${id}`,
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });
         setTemplates(response.data[0] || []);
      } catch (error) {
         console.error(error);
         setError(true);
      }
      setLoading(false);
   };

   const getExistingDefaultAlerts = async () => {
      try {
         const token = await getAccessTokenSilently({ audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}` });

         const response = await axiosInstance({
            method: 'get',
            url: `/alert/configs/${id}`,
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         setExistingAlerts(response.data[0] || []);
      } catch (error) {
         console.error('Error fetching existing default alerts:', error);
         setExistingAlerts([]);
      }
   };

   const handleEditTemplate = (templateId, field, value) => {
      setEditingTemplates(prev => ({
         ...prev,
         [templateId]: {
            ...prev[templateId],
            [field]: value
         }
      }));
   };

   const getEditedValue = (template, field) => {
      return editingTemplates[template.id]?.[field] ?? template[field];
   };

   const isDefaultAlertAlreadyExists = (template) => {
      return existingAlerts.some(alert =>
         alert.field_name === template.field_name &&
         alert.field_name_alias === template.field_name_alias &&
         alert.operator === template.operator &&
         alert.threshold === template.threshold &&
         alert.system_id === id &&
         alert.pg_table === template.pg_table &&
         (alert.user_id === 'default' || alert.user_id.toLowerCase() === 'default')
      );
   };

   const handleApplyTemplate = async (template) => {
      setApplyingTemplates(prev => ({ ...prev, [template.id]: true }));
      try {
         const token = await getAccessTokenSilently({ audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}` });

         // Get edited values or use original template values
         const editedThreshold = getEditedValue(template, 'threshold');
         const editedFrequency = getEditedValue(template, 'notify_frequency');

         const response = await axiosInstance({
            method: 'POST',
            url: `/alert/template/apply`,
            headers: {
               'Content-Type': 'application/json',
               authorization: `Bearer ${token}`,
            },
            data: JSON.stringify({
               template_id: template.id,
               system_id: id,
               user_id: user.email_address,
               threshold: editedThreshold,
               notify_frequency: editedFrequency,
               field_name: template.field_name,
               field_name_alias: template.field_name_alias,
               units_field_name: template.units_field_name,
               operator: template.operator,
               threshold_units: template.threshold_units,
               severity: template.severity,
               pg_table: template.pg_table
            }),
         });

         toaster.push(successMessage, 'topCenter');

         // Refresh existing alerts to update button states
         await getExistingDefaultAlerts();

         // Refresh parent component to update default alerts list
         if (refresh) refresh();

         // Clear editing state for this template
         setEditingTemplates(prev => {
            const newState = { ...prev };
            delete newState[template.id];
            return newState;
         });

         // Reset applying state
         setApplyingTemplates(prev => ({ ...prev, [template.id]: false }));
      } catch (error) {
         console.error(error);
         toaster.push(errorMessage, 'topCenter');
         setApplyingTemplates(prev => ({ ...prev, [template.id]: false }));
      }
   };


   const filterTemplates = () => {
      let filtered = templates;

      // Filter by system type (HHM vs Magnet)
      if (isHHM) {
         // For HHM systems, only show log-based alerts and HHM Unreachable
         filtered = filtered.filter(template => {
            const pgTable = template.pg_table || '';
            return pgTable.startsWith('log.') || pgTable === 'alert.offline_hhm_conn';
         });
      } else {
         // For Magnet systems, only show mag/edu alerts and MMB Unreachable
         filtered = filtered.filter(template => {
            const pgTable = template.pg_table || '';
            // Exclude HHM-specific alerts
            if (pgTable.startsWith('log.') || pgTable === 'alert.offline_hhm_conn') {
               return false;
            }
            // Include mag tables, edu tables, MMB Unreachable, and null pg_table templates
            return pgTable.startsWith('mag.') ||
                   pgTable.includes('mmb_') ||
                   pgTable.includes('edu') ||
                   pgTable === 'alert.offline_mmb_conn' ||
                   pgTable.includes('siemens') ||
                   pgTable.includes('philips') ||
                   pgTable.includes('ge_') ||
                   !pgTable;
         });
      }

      // Filter by search term
      if (searchTerm) {
         filtered = filtered.filter(template =>
            template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.description.toLowerCase().includes(searchTerm.toLowerCase())
         );
      }

      setFilteredTemplates(filtered);
   };

   useEffect(() => {
      getAlertTemplates();
      getExistingDefaultAlerts();
   }, []);

   useEffect(() => {
      filterTemplates();
   }, [searchTerm, templates, isHHM]);


   if (loading) return <LoadingData />;
   if (error) return <div>Something went wrong loading alert templates</div>;

   return (
      <TemplateContainer>
         <HeaderText>Alert Templates</HeaderText>
         <SubHeaderText>Browse and apply pre-configured alert templates for your system</SubHeaderText>

         <MainDivider />

         <FilterSection>
            <FlexboxGrid>
               <FlexboxGrid.Item colspan={24}>
                  <SearchContainer>
                     <Input
                        placeholder="Search templates..."
                        value={searchTerm}
                        onChange={setSearchTerm}
                        prefix={<SearchIcon />}
                     />
                  </SearchContainer>
               </FlexboxGrid.Item>
            </FlexboxGrid>
         </FilterSection>

         <MainDivider />

         {filteredTemplates.length > 0 ? (
            <>
               <ListHeader>
                  <FlexboxGrid>
                     <FlexboxGrid.Item colspan={2} align='center'>
                        SEVERITY
                     </FlexboxGrid.Item>
                     <FlexboxGrid.Item colspan={isHHM ? 7 : 6} style={{paddingLeft: 10}}>
                        NAME
                     </FlexboxGrid.Item>
                     <FlexboxGrid.Item colspan={isHHM ? 6 : 7} style={{paddingLeft: 10}}>
                        DESCRIPTION
                     </FlexboxGrid.Item>
                     {!isHHM && (
                        <FlexboxGrid.Item colspan={3} align='center'>
                           THRESHOLD
                        </FlexboxGrid.Item>
                     )}
                     <FlexboxGrid.Item colspan={isHHM ? 6 : 3} align='center'>
                        FREQUENCY
                     </FlexboxGrid.Item>
                     <FlexboxGrid.Item colspan={3} align='center'>
                        ACTION
                     </FlexboxGrid.Item>
                  </FlexboxGrid>
               </ListHeader>

               <List>
                  {filteredTemplates.map(template => (
                     <CompactListItem key={template.id}>
                        <FlexboxGrid align="middle">
                           <FlexboxGrid.Item colspan={2} align='center'>
                              <SeverityAlert severity={template.severity}>
                                 {template.severity}
                              </SeverityAlert>
                           </FlexboxGrid.Item>
                           <FlexboxGrid.Item colspan={isHHM ? 7 : 6} style={{paddingLeft: 10}}>
                              <TemplateName>{template.name}</TemplateName>
                           </FlexboxGrid.Item>
                           <FlexboxGrid.Item colspan={isHHM ? 6 : 7} style={{paddingLeft: 10}}>
                              <TemplateDescription>
                                 {template.field_name_alias} {template.operator.replace(/_/g, ' ')}
                                 {isHHM && template.threshold && template.threshold_units ?
                                    ` ${template.threshold} ${template.threshold_units}` : ''}
                              </TemplateDescription>
                           </FlexboxGrid.Item>
                           {!isHHM && (
                              <FlexboxGrid.Item colspan={3} align='left'>
                                 <LeftAlignedThresholdContainer>
                                    {template.field_name === 'comp_vib_status' ? (
                                       <ReadOnlyThreshold>
                                          {template.threshold}
                                       </ReadOnlyThreshold>
                                    ) : (
                                       <EditableInput
                                          size="sm"
                                          placeholder={template.threshold}
                                          value={getEditedValue(template, 'threshold')}
                                          onChange={(value) => handleEditTemplate(template.id, 'threshold', value)}
                                       />
                                    )}
                                    <ThresholdUnits>
                                       {template.threshold_units || ''}
                                    </ThresholdUnits>
                                 </LeftAlignedThresholdContainer>
                              </FlexboxGrid.Item>
                           )}
                           <FlexboxGrid.Item colspan={isHHM ? 6 : 3} align='center'>
                              <EditableFrequencyContainer>
                                 <EditableInputNumber
                                    size="sm"
                                    min={1}
                                    max={168}
                                    value={getEditedValue(template, 'notify_frequency')}
                                    onChange={(value) => handleEditTemplate(template.id, 'notify_frequency', value)}
                                 />
                                 <FrequencyLabel>hrs</FrequencyLabel>
                              </EditableFrequencyContainer>
                           </FlexboxGrid.Item>
                           <FlexboxGrid.Item colspan={3} align='center'>
                              {isDefaultAlertAlreadyExists(template) ? (
                                 <IconButton
                                    icon={<AddOutlineIcon />}
                                    color="grey"
                                    appearance="ghost"
                                    size="sm"
                                    disabled
                                 >
                                    Exists
                                 </IconButton>
                              ) : (
                                 <IconButton
                                    icon={<AddOutlineIcon />}
                                    color="green"
                                    appearance="primary"
                                    size="sm"
                                    onClick={() => handleApplyTemplate(template)}
                                    disabled={applyingTemplates[template.id]}
                                    loading={applyingTemplates[template.id]}
                                 >
                                    Apply
                                 </IconButton>
                              )}
                           </FlexboxGrid.Item>
                        </FlexboxGrid>
                     </CompactListItem>
                  ))}
               </List>
            </>
         ) : (
            <NoTemplatesMessage>
               {searchTerm
                  ? 'No templates match your search'
                  : 'No alert templates available for this system'}
            </NoTemplatesMessage>
         )}
      </TemplateContainer>
   );
};

const TemplateContainer = styled.div`
   width: 85vw;
   margin: 0 auto;
`;

const HeaderText = styled.div`
   width: 100%;
   font-size: 1.5em;
   font-weight: bold;
   margin-bottom: 0.5em;
   @media screen and (max-width: 769px) {
      font-size: 1.2em;
   }
`;

const SubHeaderText = styled.div`
   width: 100%;
   font-size: 1em;
   color: #666;
   margin-bottom: 1em;
   @media screen and (max-width: 769px) {
      font-size: 0.85em;
   }
`;

const FilterSection = styled.div`
   margin: 1em 0;
`;

const SearchContainer = styled.div`
   display: flex;
   justify-content: flex-end;
`;

const TemplateName = styled.div`
   font-weight: bold;
   font-size: 0.9em;
`;

const TemplateDescription = styled.div`
   color: #666;
   font-size: 0.8em;
`;


const NoTemplatesMessage = styled.div`
   text-align: center;
   padding: 2em;
   color: #666;
   font-style: italic;
`;

const CompactListItem = styled(List.Item)`
   .rs-list-item-content {
      padding: 4px 8px !important;
      min-height: auto !important;
   }
   border-bottom: 1px solid #e5e5ea;
   &:hover {
      background-color: #f7f7fa;
   }
`;

const LeftAlignedThresholdContainer = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   justify-content: flex-start;
   width: 100%;
`;

const EditableInput = styled(Input)`
   width: 70px;
   .rs-input {
      text-align: center;
      font-size: 0.8em;
      padding: 1px 4px;
   }
`;

const EditableInputNumber = styled(InputNumber)`
   width: 65px;
   .rs-input-number-input {
      text-align: center;
      font-size: 0.8em;
      padding: 1px 4px;
   }
`;

const EditableFrequencyContainer = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   justify-content: center;
`;

const ThresholdUnits = styled.span`
   font-size: 0.75em;
   color: #666;
   font-weight: bold;
`;

const FrequencyLabel = styled.span`
   font-size: 0.75em;
   color: #666;
   font-weight: bold;
`;

const ReadOnlyThreshold = styled.span`
   display: inline-block;
   width: 70px;
   text-align: center;
   font-size: 0.8em;
   padding: 3px;
   background-color: #f7f7fa;
   border: 1px solid #e5e5ea;
   border-radius: 4px;
   color: #555;
`;


const successMessage = (
   <Message showIcon type="success" header="Success">
      Alert template applied successfully.
   </Message>
);

const errorMessage = (
   <Message showIcon type="error" header="Error">
      Failed to apply alert template. Please try again.
   </Message>
);